// src/components/ProtectedRoute.tsx

import React, { useState, useEffect, ReactNode } from 'react'; // Import ReactNode
import { Navigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { SHOPIFY_STORES } from '../config';

// 1. Define the props interface
interface ProtectedRouteProps {
    children: ReactNode; // 'children' must be of type ReactNode (any valid JSX element)
}

// 2. Apply the interface to the component
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
    // You can also type it directly like this:
    // const ProtectedRoute = ({ children }: { children: ReactNode }) => {

    const [isChecking, setIsChecking] = useState(true);
    const [isLoggedIn, setIsLoggedIn] = useState(false);


    useEffect(() => {
        // Read token from localStorage first (more reliable in some iOS webviews),
        // then fall back to cookies.
        const tokenFromCookie = Cookies.get('customerAccessToken');
        const tokenFromLocalStorage = typeof window !== 'undefined' ? localStorage.getItem('customerAccessToken') : null;
        const tokenToValidate = tokenFromLocalStorage || tokenFromCookie;

        if (!tokenToValidate) {
            setIsChecking(false);
            return; // Not logged in, will redirect below
        }

        // --- Session Validation Logic ---
        const validateSession = async () => {
            // Get the store key the user logged in with
            const storeKey = localStorage.getItem('loggedInStoreKey') || Cookies.get('loggedInStoreKey') || 'USA';
            const storeConfig = SHOPIFY_STORES[storeKey as keyof typeof SHOPIFY_STORES] || SHOPIFY_STORES.USA;
            
            try {
                // Validate token against the correct store
                const customerRes = await fetch(storeConfig.url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Shopify-Storefront-Access-Token': storeConfig.token,
                    },
                    body: JSON.stringify({
                        query: `query getCustomer($customerAccessToken: String!) { customer(customerAccessToken: $customerAccessToken) { id } }`,
                        variables: { customerAccessToken: tokenToValidate },
                    }),
                });

                const result = await customerRes.json();
                const customerId = result.data?.customer?.id;

                if (customerId) {
                    setIsLoggedIn(true);
                } else {
                    // Token is expired or invalid on the server: clear both cookie and localStorage
                    Cookies.remove('customerAccessToken');
                    Cookies.remove('isLoggedIn');
                    Cookies.remove('loggedInStoreKey');
                    try {
                        localStorage.removeItem('customerAccessToken');
                        localStorage.removeItem('isLoggedIn');
                        localStorage.removeItem('loggedInStoreKey');
                    } catch (e) {
                        // ignore
                    }
                    setIsLoggedIn(false);
                }
            } catch (error) {
                console.error("Session validation error:", error);
                Cookies.remove('customerAccessToken');
                Cookies.remove('isLoggedIn');
                Cookies.remove('loggedInStoreKey');
                try {
                    localStorage.removeItem('customerAccessToken');
                    localStorage.removeItem('isLoggedIn');
                    localStorage.removeItem('loggedInStoreKey');
                } catch (e) {
                    // ignore
                }
                setIsLoggedIn(false);
            } finally {
                setIsChecking(false);
            }
        };

        validateSession();
    }, []);

    // --- Render Logic ---
    if (isChecking) {
        // Show a loading/splash screen while validating the token
        return <div></div>;
    }

    if (!isLoggedIn) {
        // Redirect to sign in if not logged in after check
        return <Navigate to="/SignIn" replace />;
    }

    // Render the protected content (children)
    return <>{children}</>;
};

export default ProtectedRoute;